import { useState, useEffect, useRef } from "react";
// import { FaCamera } from "react-icons/fa";
import DocumentPreviewModal from "../modal/DocumentPreviewModal";
import "./employee.css";
import { FaFilePdf, FaFileImage, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileAlt, FaCloudUploadAlt, FaExclamationTriangle } from "react-icons/fa";
import Can from "../common/can";
import { toast } from "react-toastify";
import { updateEmployee, recordEmployeeExit, uploadEmployeeDocument, deleteEmployeeDocument } from "../services/employeeservice";
import { useAuth } from "../context/authcontext";
import ConfirmModal from "./delete_modal";
import Dropdown from "../modal/dropdown";
import {
  DocumentTypeFormOptions,
  getDocumentTypeLabel,
  getGenderLabel,
  getEmployeeStatusLabel,
  getEmployeeStatusClass,
  EmployeeStatus,
} from "../../constants/constants";

const formatBirthdayDisplay = (birthdayStr, dobStr) => {
  if (birthdayStr) return birthdayStr;
  if (!dobStr) return null;
  try {
    const parts = dobStr.split("-");
    if (parts.length === 3) {
      const date = new Date(parts[0], parseInt(parts[1], 10) - 1, parts[2]);
      return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
    }
  } catch (e) {}
  return dobStr;
};

/* Per-extension icon + color mapping — kept outside the component so it
   isn't recreated on every render. */
function getDocIconMeta(ext) {
  if (ext === "pdf") return { Icon: FaFilePdf, cls: "pdf" };
  if (ext === "png") return { Icon: FaFileImage, cls: "png" };
  if (["jpg", "jpeg"].includes(ext)) return { Icon: FaFileImage, cls: "jpg" };
  if (["gif", "webp", "svg"].includes(ext)) return { Icon: FaFileImage, cls: "gif" };
  if (["doc", "docx"].includes(ext)) return { Icon: FaFileWord, cls: "doc" };
  if (["xls", "xlsx", "csv"].includes(ext)) return { Icon: FaFileExcel, cls: "xls" };
  if (["ppt", "pptx"].includes(ext)) return { Icon: FaFilePowerpoint, cls: "ppt" };
  return { Icon: FaFileAlt, cls: "generic" };
}

const formatDesignation = (emp) => {
  if (!emp) return "-";
  if (typeof emp.designation === "object" && emp.designation) return emp.designation.name || "-";
  return emp.designation_name || (typeof emp.designation === "string" ? emp.designation : "-");
};

const formatDepartment = (emp) => {
  if (!emp) return "-";
  if (typeof emp.department === "object" && emp.department) return emp.department.name || "-";
  return emp.department_name || (typeof emp.department === "string" ? emp.department : "-");
};

const formatManager = (emp) => {
  if (!emp) return "-";
  if (typeof emp.reporting_manager === "object" && emp.reporting_manager) {
    return emp.reporting_manager.full_name || emp.reporting_manager.name || emp.reporting_manager.emp_id || "-";
  }
  return emp.reporting_manager_name || (typeof emp.reporting_manager === "string" ? emp.reporting_manager : "-");
};

/* ── Resolve a display label for employee status. Matches the pattern used
   on the Employee list page: map the numeric status straight through the
   canonical EmployeeStatusMeta lookup rather than trusting a possibly-stale
   server-provided status_display string. ── */
const formatEmployeeStatus = (emp) => getEmployeeStatusLabel(emp?.status);

const ViewEmployee = ({ employee, onClose, onRefresh }) => {
  const { user, hasPermission, hasAnyPermission, hasRole } = useAuth();
  const isAdmin = hasRole("admin");

  const [tab, setTab] = useState("job");
  const [profileImage, setProfileImage] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [uploading, setUploading] = useState(false);
  const [deleteDocTarget, setDeleteDocTarget] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [showExitForm, setShowExitForm] = useState(false);
  const [exitData, setExitData] = useState({
    status: "resigned",
    exit_date: new Date().toISOString().split("T")[0],
    exit_reason: "",
  });

  const [docType, setDocType] = useState(DocumentTypeFormOptions[0]?.value ?? "");
  const [docFile, setDocFile] = useState(null);
  const [docRemarks, setDocRemarks] = useState("");
  const [docUploading, setDocUploading] = useState(false);
  const docFileInputRef = useRef(null);

  // Scrolls the separation panel into view once it expands, since the
  // popup body doesn't auto-scroll to newly-rendered content on its own.
  const separationRef = useRef(null);

  useEffect(() => {
    if (showExitForm && separationRef.current) {
      separationRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showExitForm]);

  const isSelf = user?.employee === employee.id || user?.employee_profile?.id === employee.id || user?.id === employee.user_id || user?.employee_id === employee.id;
  const canViewDoc = isAdmin || hasAnyPermission(["document.view", "employee.view_documents", "employee.view_sensitive_documents", "employee.update"]) || isSelf;
  const canUploadDoc = isAdmin || hasAnyPermission(["document.upload", "employee.upload_documents", "employee.update"]) || (isSelf && hasPermission("employee.upload_self_document"));
  const canDeleteDoc = isAdmin || hasAnyPermission(["document.delete", "employee.delete_documents", "employee.update"]) || (isSelf && hasPermission("employee.upload_self_document"));

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!docFile) {
      return toast.error("Please select a file to upload");
    }
    try {
      setDocUploading(true);
      const formData = new FormData();
      formData.append("document_type", docType);
      formData.append("file", docFile);
      if (docRemarks) formData.append("remarks", docRemarks);
      await uploadEmployeeDocument(employee.id, formData);
      toast.success("Document uploaded successfully");
      setDocFile(null);
      setDocRemarks("");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload document");
    } finally {
      setDocUploading(false);
    }
  };

  const confirmDeleteDoc = async (password) => {
    try {
      await deleteEmployeeDocument(employee.id, deleteDocTarget.id, password);
      toast.success("Document deleted successfully");
      setDeleteDocTarget(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.detail || err.message || "Failed to delete document";
      throw new Error(msg);
    }
  };

  const handleRecordExit = async () => {
    try {
      await recordEmployeeExit(employee.id, exitData);
      toast.success("Employee exit recorded successfully. Linked user account deactivated.");
      setShowExitForm(false);
      if (onRefresh) onRefresh();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to record exit");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setProfileImage(preview);

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("photo", file);

      await updateEmployee(employee.id, formData);
      toast.success("Profile picture updated successfully!");
    } catch (err) {
      toast.error("Failed to upload profile picture");
      setProfileImage(null);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (profileImage) {
        URL.revokeObjectURL(profileImage);
      }
    };
  }, [profileImage]);

  const hasDocuments = employee.documents?.length > 0;

  return (
    <div className="popup-overlay" >
      <div className="popup-content premium-profile" onClick={(e) => e.stopPropagation()}>

        <div className="profile-header-card">
          <div className="avatar-wrapper">
            {profileImage || employee.photo_url || employee.photo ? (
              <img src={profileImage || employee.photo_url || employee.photo} className="avatar-img" alt="Profile" />
            ) : (
              <div className="avatar-letter">
                {employee.full_name?.charAt(0)?.toUpperCase()}
              </div>
            )}
            
          </div>

          <div className="employee-info">
            <h2>{employee.full_name}</h2>
            <p className="employee-role">
              {formatDesignation(employee)}
              {" • "}
              {formatDepartment(employee)}
            </p>
            <div className="employee-contact">
              <span>{employee.email}</span>
              <span>{employee.phone}</span>
            </div>
            <span className={`emp-status ${getEmployeeStatusClass(employee.status)}`}>
              {formatEmployeeStatus(employee)}
            </span>
          </div>

          <button className="profile-close" onClick={onClose}>Back</button>
        </div>

        <div className="profile-tabs">
          <button className={tab === "job" ? "active" : ""} onClick={() => setTab("job")}>Job Details</button>
          <Can perm="employee.view_personal_details">
            <button className={tab === "personal" ? "active" : ""} onClick={() => setTab("personal")}>Personal Details</button>
          </Can>
          <Can perm="employee.view_bank_details">
            <button className={tab === "bank" ? "active" : ""} onClick={() => setTab("bank")}>Bank Details</button>
          </Can>
          {canViewDoc && (
            <button className={tab === "docs" ? "active" : ""} onClick={() => setTab("docs")}>Documents</button>
          )}
        </div>

        {tab === "job" && (
          <>
            <div className="profile-grid">
              <div className="profile-card">
                <label>Department</label>
                <span>{formatDepartment(employee)}</span>
              </div>
              <div className="profile-card">
                <label>Designation</label>
                <span>{formatDesignation(employee)}</span>
              </div>
              <div className="profile-card">
                <label>Date of Joining</label>
                <span>{employee.date_of_joining || employee.joining_date}</span>
              </div>
              <div className="profile-card">
                <label>Reporting Manager</label>
                <span>{formatManager(employee)}</span>
              </div>
              <div className="profile-card">
                <label>Employment Status</label>
                <span style={{ textTransform: "capitalize", fontWeight: 600 }}>
                  {formatEmployeeStatus(employee)}
                </span>
              </div>
              {(employee.status === EmployeeStatus.RESIGNED || employee.status === EmployeeStatus.TERMINATED) && employee.exit_date && (
                <div className="profile-card">
                  <label>Exit Date</label>
                  <span>{employee.exit_date}</span>
                </div>
              )}
              {(employee.status === EmployeeStatus.RESIGNED || employee.status === EmployeeStatus.TERMINATED) && employee.exit_reason && (
                <div className="profile-card" style={{ gridColumn: "1 / -1" }}>
                  <label>Exit Reason / Remarks</label>
                  <span>{employee.exit_reason}</span>
                </div>
              )}
            </div>

            <Can perm="employee.exit">
              <div className="separation-card" ref={separationRef}>
                {!showExitForm ? (
                  <button type="button" className="separation-trigger-premium" onClick={() => setShowExitForm(true)}>
                    <span className="separation-trigger-icon"><FaExclamationTriangle /></span>
                    <div className="separation-trigger-text">
                      <strong>Record Resignation / Termination</strong>
                     
                    </div>
                    <span className="separation-trigger-arrow">›</span>
                  </button>
                ) : (
                  <div className="separation-panel">
                    <div className="separation-panel-header">
                      <span className="icon-circle"><FaExclamationTriangle /></span>
                      <h4>Record Employee Separation</h4>
                    </div>
                    <p className="hint">
                     
                    </p>
                    <div className="separation-fields-grid">
                      <div className="separation-field">
                        <label>Separation Type</label>
                        <Dropdown
                          options={[
                            { label: "Resigned", value: "resigned" },
                            { label: "Terminated", value: "terminated" },
                          ]}
                          value={exitData.status}
                          onChange={(value) => setExitData({ ...exitData, status: value })}
                          placeholder="Separation Type"
                        />
                      </div>
                      <div className="separation-field">
                        <label>Exit Date</label>
                        <input
                          type="date"
                          value={exitData.exit_date}
                          onChange={(e) => setExitData({ ...exitData, exit_date: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="separation-field" style={{ marginBottom: 16 }}>
                      <label>Reason / Remarks</label>
                      <textarea
                        rows="3"
                        value={exitData.exit_reason}
                        onChange={(e) => setExitData({ ...exitData, exit_reason: e.target.value })}
                        placeholder="Enter detailed reason for resignation or termination..."
                      />
                    </div>
                    <div className="separation-actions">
                      <button type="button" className="separation-confirm-btn" onClick={handleRecordExit}>Confirm Exit</button>
                      <button type="button" className="secondary-btn" onClick={() => setShowExitForm(false)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </Can>
          </>
        )}

        {tab === "personal" && (
          <div className="profile-grid">
            <div className="profile-card">
              <label>Birthday</label>
              <span>{formatBirthdayDisplay(employee.birthday, employee.date_of_birth) || "— (Not Provided)"}</span>
            </div>
            <div className="profile-card">
              <label>Gender</label>
              <span>{employee.gender ? getGenderLabel(employee.gender) : "— (Not Provided)"}</span>
            </div>
            <div className="profile-card">
              <label>Emergency Contact Name</label>
              <span>{employee.emergency_contact_name || "— (Not Provided)"}</span>
            </div>
            <div className="profile-card">
              <label>Emergency Contact Number</label>
              <span>{employee.emergency_contact_number || "— (Not Provided)"}</span>
            </div>
          </div>
        )}

        {tab === "bank" && (
          <div className="profile-grid">
            <div className="profile-card">
              <label>Account Number</label>
              <span style={{ fontWeight: employee.bank_account_number ? "600" : "400", color: employee.bank_account_number ? "#1e293b" : "#94a3b8" }}>
                {employee.bank_account_number || "— (Not Provided)"}
              </span>
            </div>
            <div className="profile-card">
              <label>IFSC Code</label>
              <span style={{ fontWeight: employee.ifsc_code ? "600" : "400", color: employee.ifsc_code ? "#1e293b" : "#94a3b8" }}>
                {employee.ifsc_code || "— (Not Provided)"}
              </span>
            </div>
            <div className="profile-card">
              <label>Bank Name</label>
              <span style={{ fontWeight: employee.bank_name ? "600" : "400", color: employee.bank_name ? "#1e293b" : "#94a3b8" }}>
                {employee.bank_name || "— (Not Provided)"}
              </span>
            </div>
          </div>
        )}

        {tab === "docs" && (
          <div>
            {canUploadDoc && (
              <form onSubmit={handleUploadDocument} className="doc-upload-form-premium">
                <h4> Upload New Document</h4>
                <div className="doc-upload-fields">
                  <Dropdown options={DocumentTypeFormOptions} value={docType} onChange={setDocType} placeholder="Document Type" />
                  <label className="file-choose-premium" htmlFor="doc-upload-file">
                    <span className="file-choose-icon"><FaCloudUploadAlt /></span>
                    <span className={`file-choose-text ${docFile ? "has-file" : ""}`}>
                      {docFile?.name || "Choose a file to upload"}
                    </span>
                  </label>
                  <input
                    id="doc-upload-file"
                    type="file"
                    ref={docFileInputRef}
                    className="hidden-file-input"
                    onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                  />
                 <button
  type="submit"
  disabled={docUploading}
  className="doc-upload-btn"
>
  {docUploading ? "Uploading..." : " Upload "}
</button>
                </div>
              </form>
            )}

            <div className="docs-list">
              {hasDocuments ? (
                (employee.documents || []).map((doc, index) => {
                  const fileName = doc.original_filename || "file";
                  const fileUrl = doc.file_url || "#";
                  const ext = fileName?.split(".").pop()?.toLowerCase() || "";
                  const { Icon, cls } = getDocIconMeta(ext);

                  return (
                    <div key={index} className="doc-item">
                      <div className="doc-left">
                        <span className={`doc-icon-badge ${cls}`}>
                          <Icon />
                        </span>
                        <div>
                          <span className="doc-type">{getDocumentTypeLabel(doc.document_type)}</span>
                          <div className="doc-filename">{fileName}</div>
                        </div>
                      </div>

                      <div className="doc-actions">
                        <button type="button" onClick={() => setPreviewDoc(doc)} className="doc-preview">Preview</button>
                        <button
                          type="button"
                          className="doc-download"
                           onClick={() => {
                              const url = doc.download_url || fileUrl;
                              const link = document.createElement("a");
                              link.href = url;
                              link.download = fileName;
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                          }}
                        >Download</button>
                        {canDeleteDoc && (
                          <button type="button" onClick={() => setDeleteDocTarget(doc)} className="doc-delete-btn">
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="empty-state">
                  <img src="/images/nodocument.svg" alt="No documents" className="empty-img" />
                  <p>No Documents Uploaded</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* DELETE DOCUMENT MODAL */}
        {deleteDocTarget && (
          <ConfirmModal
            title="Delete Document"
            message={`Are you sure you want to delete "${deleteDocTarget.original_filename || deleteDocTarget.document_type || "this document"}"?`}
            confirmText="Delete"
            type="danger"
            onConfirm={confirmDeleteDoc}
            onCancel={() => setDeleteDocTarget(null)}
          />
        )}

        {/* PREVIEW DOCUMENT MODAL */}
        <DocumentPreviewModal 
          doc={previewDoc} 
          onClose={() => setPreviewDoc(null)} 
        />

      </div>
    </div>
  );
};

export default ViewEmployee;